import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  ExternalLink,
  NamedLink,
  IconSpinner,
} from '../../components';
import AttachmentList from '../AttachmentList/AttachmentList';
import CustomOfferConfirmStep from './CustomOfferConfirmStep';
import VatExplanation from '../VatExplanation/VatExplanation';
import { HiLightBulb } from 'react-icons/hi';
import BillingDetailsForm, {
  initialValuesFromBilling,
} from '../../forms/BillingDetailsForm/BillingDetailsForm';
import { GATE_STATUS, gateStatusFromError } from '../../util/vatGate';
import {
  offerTotals,
  resolutionForOffer,
  rateLabelFor,
  expectedPreviewPath,
  applicablePreview,
  vatFromTransaction,
  FEE_MODEL,
} from '../../util/vat';

import css from './TransactionPanel.css';

const { Money } = sdkTypes;

// The schedule card names full dates -- an offer's start and delivery can fall
// in different years, and formatDateOnly drops the year.
const formatFullDate = (intl, isoDate) =>
  intl.formatDate(new Date(isoDate), { year: 'numeric', month: 'numeric', day: 'numeric' });

const VERO_URL =
  'https://www.vero.fi/en/detailed-guidance/guidance/48617/how_to_submit_the_vat_eu_recapitulative/';
const HOLD_RETRY_MS = 30000;

const blockedNotice = (reason, isCustomer) => {
  const base = `VatPreview.blocked.${reason}`;
  const messageId =
    reason === 'CROSS_BORDER_B2B_NEEDS_SELLER_VAT_ID'
      ? `${base}.${isCustomer ? 'customer' : 'provider'}`
      : base;
  return { kind: 'blocked', messageId, values: {} };
};

// What to say above the price box (spec §5 "Preview and gates"). The
// provider hears only about what they can fix themselves: a block, or their
// own incomplete tax information (R38).
export const vatNoticeFor = (preview, isCustomer, error = null) => {
  if (!isCustomer) {
    if (!preview) return null;
    if (preview.status === GATE_STATUS.BLOCKED) return blockedNotice(preview.reason, false);
    if (preview.status === GATE_STATUS.PROVIDER_INCOMPLETE)
      return { kind: 'blocked', messageId: 'VatPreview.providerIncompleteSelf', values: {} };
    return null;
  }
  if (!preview) {
    if (error) return { kind: 'hold', messageId: 'VatPreview.fetchFailed', values: {} };
    return null;
  }
  const { status, reason } = preview;
  if (status === GATE_STATUS.HOLD)
    return { kind: 'hold', messageId: 'VatPreview.hold', values: {} };
  if (status === GATE_STATUS.NEEDS_BILLING)
    return { kind: 'billing', messageId: 'VatPreview.needsBilling', values: {} };
  if (status === GATE_STATUS.PROVIDER_INCOMPLETE)
    return { kind: 'blocked', messageId: 'VatPreview.providerIncomplete', values: {} };
  if (status === GATE_STATUS.BLOCKED) return blockedNotice(reason, true);
  // An ok preview needs no notice: the confirm step's VatExplanation shows
  // the billing details used, an unverified number included (R37, spec §4),
  // and explains the VAT in one sentence (spec §2).
  return null;
};

const CustomOfferModal = ({
  isOpen,
  onClose,
  onManageDisableScrolling,
  customOfferData,
  isCustomer,
  intl,
  onAcceptCustomOffer,
  acceptOfferInProgress,
  onUpdateOffer,
  transactionLastTransition,
  onDownloadAttachment = () => null,
  onOpenAttachment = () => null,
  onFetchAttachmentPreview = () => null,
  attachmentPreviewUrls = {},
  downloadAttachmentInProgressIds = [],
  invoiceOption = null,
  paymentTermDays = 14,
  onRequestInvoicing = () => null,
  acceptOfferError = null,
  vatPreview = null,
  vatPreviewInProgress = false,
  vatPreviewError = null,
  onRetryVatPreview = () => null,
  onPaymentMethodChange = () => null,
  onSaveBillingDetails = () => Promise.resolve(),
  saveBillingDetailsInProgress = false,
  saveBillingDetailsError = null,
  billingInitialValues = null,
  lockedBillingFields = [],
}) => {
  // Payment method for a pending offer. Invoice is preselected whenever the
  // customer may use it (an approved customer, or one whose request was just
  // approved while this modal stayed mounted); otherwise card. Invoice can
  // only stay selected while the option is available.
  const [paymentMethod, setPaymentMethod] = useState(
    invoiceOption === 'available' ? 'invoice' : 'card'
  );
  // 'offer' (choose how to pay, prices without VAT) or 'confirm' (invoice
  // only: the VAT-inclusive amounts and the accept), spec §2–3.
  const [step, setStep] = useState('offer');
  const previousInvoiceOption = useRef(invoiceOption);
  useEffect(() => {
    const becameAvailable =
      invoiceOption === 'available' && previousInvoiceOption.current !== 'available';
    previousInvoiceOption.current = invoiceOption;
    if (becameAvailable) setPaymentMethod('invoice');
    else if (paymentMethod === 'invoice' && invoiceOption !== 'available') setPaymentMethod('card');
  }, [invoiceOption, paymentMethod]);

  // Only a preview fetched for the selected payment method prices and gates
  // the offer (R35).
  const expectedPath = expectedPreviewPath(
    customOfferData && customOfferData.processName,
    paymentMethod
  );
  const preview = applicablePreview(vatPreview, expectedPath);
  // Legacy pro offers accept through a non-privileged transition that never
  // resolves VAT, so they are neither previewed nor gated (R39).
  const isLegacyPro = !!(customOfferData && customOfferData.processName === 'flex-pro-process');
  // Check if offer is pending (not yet accepted). Computed up front (it only
  // reads the transactionLastTransition prop) so the stale-preview safety
  // net effect below can use it ahead of the early return further down.
  const isPending =
    transactionLastTransition === 'transition/make-custom-offer' ||
    transactionLastTransition === 'transition/make-custom-pro-offer';
  const vatGated = isPending && !isLegacyPro;

  // One preview fetch per open, for the method selected at that moment.
  const wasOpen = useRef(false);
  // The method-change effect further below owns refetching when the
  // customer switches payment method; the safety net effect needs to see
  // the pre-switch value (see its comment), so this ref is declared here
  // and the effect that mutates it stays in place, after the safety net.
  const previousMethod = useRef(paymentMethod);

  // Stale-response safety net (R41): responses can land out of order (e.g.
  // the panel's card-path fetch resolving after this modal's invoice-path
  // fetch, since the first VIES lookup for a business customer can take
  // seconds). If the preview in state is for another payment method than
  // the one selected, and nothing is already in flight or erroring, refetch
  // for the selected method -- once per stale preview object, so this
  // cannot loop.
  //
  // Declared (and so run) before the open-edge effect and the method-change
  // effect below, so it still reads wasOpen.current and previousMethod.current
  // from before this commit. It yields to both: the render that opens the
  // modal is left to the open-edge effect's own fetch (the preview present
  // at that moment is recorded as already covered rather than fetched
  // twice), and a render where the customer switches method is left to the
  // method-change effect (both call the same fetch in the panel, so firing
  // both here would duplicate a VIES-backed request on every switch).
  const lastRefetchedStalePreview = useRef(null);
  useEffect(() => {
    if (!isOpen || !vatGated) return;
    if (!wasOpen.current) {
      lastRefetchedStalePreview.current = vatPreview;
      return;
    }
    if (previousMethod.current !== paymentMethod) return;
    const stale = !!vatPreview && !preview && !vatPreviewInProgress && !vatPreviewError;
    if (stale && lastRefetchedStalePreview.current !== vatPreview) {
      lastRefetchedStalePreview.current = vatPreview;
      onRetryVatPreview(paymentMethod);
    }
  }, [
    isOpen,
    vatGated,
    vatPreview,
    preview,
    vatPreviewInProgress,
    vatPreviewError,
    paymentMethod,
    onRetryVatPreview,
  ]);

  useEffect(() => {
    if (isOpen && !wasOpen.current && !isLegacyPro) onRetryVatPreview(paymentMethod);
    wasOpen.current = isOpen;
  }, [isOpen, isLegacyPro, paymentMethod, onRetryVatPreview]);

  // Hold: VIES has not answered yet. Retry on a timer while the confirm step
  // shows the wait; the offer step says nothing about it, and the checkout
  // keeps its own timer.
  useEffect(() => {
    if (!isOpen || step !== 'confirm' || !preview || preview.status !== GATE_STATUS.HOLD) {
      return undefined;
    }
    const t = setTimeout(() => onRetryVatPreview(paymentMethod), HOLD_RETRY_MS);
    return () => clearTimeout(t);
  }, [isOpen, step, preview, paymentMethod, onRetryVatPreview]);

  useEffect(() => {
    if (previousMethod.current !== paymentMethod) {
      previousMethod.current = paymentMethod;
      if (!isLegacyPro) onPaymentMethodChange(paymentMethod);
    }
  }, [paymentMethod, isLegacyPro, onPaymentMethodChange]);

  // Every open starts at the offer step, and so does a method that is no
  // longer invoice (the confirm step exists for the invoice path only).
  useEffect(() => {
    if (!isOpen || paymentMethod !== 'invoice') setStep('offer');
  }, [isOpen, paymentMethod]);

  if (!customOfferData) return null;
  const attachments = customOfferData.attachments || [];

  // A pending offer prices on the selected method's path only; an accepted
  // one prints its stored line items whatever the method toggle says
  // (the display rule, customer service fee spec §1).
  const offerPath = isPending ? expectedPath : undefined;
  const resolution = resolutionForOffer(
    { attributes: { protectedData: { vat: customOfferData.vat } } },
    vatPreview,
    offerPath
  );
  const totals = offerTotals({
    lineItems: customOfferData.lineItems,
    isPending,
    resolution,
    customerPrice: customOfferData.amount,
  });
  const supplies = resolution.supplies || {};
  const customerSupply = supplies.core || supplies.customerSale || null;
  const providerSupply = supplies.core || supplies.providerSale || null;
  const feeSupply = supplies.serviceFee || null;
  // Transactions made under the provider commission keep their old rows; their
  // fee VAT label comes from the v1 snapshot's platform fee.
  const isProviderFeeModel = totals.feeModel === FEE_MODEL.PROVIDER;
  const legacyFeeSupply = vatFromTransaction({
    attributes: { protectedData: { vat: customOfferData.vat } },
  }).platformFee;
  const money = cents => formatMoney(intl, new Money(cents, 'EUR'));
  const customerRateLabel = rateLabelFor(customerSupply, intl.locale);
  const providerRateLabel = rateLabelFor(providerSupply, intl.locale);
  const feeRateLabel = rateLabelFor(feeSupply, intl.locale);
  // One VAT row when the work and the fee carry the same rate label, two
  // otherwise (spec §5). Under the provider commission the customer has no fee VAT.
  const customerVatRows =
    isProviderFeeModel || customerRateLabel === feeRateLabel
      ? [
          {
            key: 'vat',
            messageId: 'BookingBreakdown.vatLine',
            rateLabel: customerRateLabel,
            amount: totals.customerVat + (isProviderFeeModel ? 0 : totals.feeVat),
          },
        ]
      : [
          {
            key: 'vat',
            messageId: 'BookingBreakdown.vatLine',
            rateLabel: customerRateLabel,
            amount: totals.customerVat,
          },
          {
            key: 'feeVat',
            messageId: 'BookingBreakdown.vatOnFee',
            rateLabel: feeRateLabel,
            amount: totals.feeVat,
          },
        ];

  const invoiceNoteId = {
    request: 'CustomOfferModal.invoiceRequest',
    pending: 'CustomOfferModal.invoicePending',
    denied: 'CustomOfferModal.invoiceDenied',
    unsupported: 'CustomOfferModal.invoiceUnsupported',
  }[invoiceOption];
  const invoiceSelectable = invoiceOption === 'available';
  const invoiceDisabled = ['pending', 'denied', 'unsupported'].includes(invoiceOption);
  const onInvoiceClick = () => {
    if (invoiceSelectable) setPaymentMethod('invoice');
    else if (invoiceOption === 'request') onRequestInvoicing();
  };

  const notice = vatGated ? vatNoticeFor(preview, isCustomer, vatPreviewError) : null;
  const vatReady = !vatGated || !!(preview && preview.status === GATE_STATUS.OK);
  const noticeValues = {
    a: chunks => <NamedLink name="TaxInformationPage">{chunks}</NamedLink>,
    vero: chunks => <ExternalLink href={VERO_URL}>{chunks}</ExternalLink>,
  };
  // The accept itself hit a VAT gate (409 { code, reason }): name the gate
  // rather than a generic failure.
  const acceptGateStatus = gateStatusFromError(acceptOfferError);
  const acceptGateNotice = acceptGateStatus
    ? vatNoticeFor({ status: acceptGateStatus, reason: acceptOfferError.reason }, isCustomer)
    : null;
  // A live hold: VIES has not answered and the timer above keeps asking, so
  // the notice carries a spinner and the retry is the secondary action. A
  // failed fetch (same kind) retries only by hand and keeps the primary one.
  const isHeld = !!preview && preview.status === GATE_STATUS.HOLD;
  // Right after the customer saves billing details the preview is still
  // being fetched: show that check instead of the form they just submitted.
  const checkingBilling = !!notice && notice.kind === 'billing' && vatPreviewInProgress;
  const showSpinner =
    (notice && notice.kind === 'hold' && (isHeld || vatPreviewInProgress)) || checkingBilling;
  const RetryButton = isHeld ? SecondaryButton : PrimaryButton;
  const vatNoticeBlock = notice ? (
    <div className={css.modalSection}>
      <div
        className={classNames(css.vatNotice, { [css.vatNoticeBlocked]: notice.kind === 'blocked' })}
        role="status"
        aria-live="polite"
      >
        <div className={css.vatNoticeRow}>
          {showSpinner ? <IconSpinner rootClassName={css.vatNoticeSpinner} /> : null}
          <p className={css.vatNoticeText}>
            <FormattedMessage
              id={checkingBilling ? 'VatPreview.checking' : notice.messageId}
              values={noticeValues}
            />
          </p>
        </div>
        {notice.kind === 'hold' ? (
          <RetryButton
            type="button"
            className={css.vatNoticeButton}
            onClick={() => onRetryVatPreview(paymentMethod)}
            inProgress={vatPreviewInProgress}
            disabled={vatPreviewInProgress}
          >
            <FormattedMessage id="VatPreview.holdRetry" />
          </RetryButton>
        ) : null}
        {isCustomer && !checkingBilling && notice.kind === 'billing' ? (
          <BillingDetailsForm
            compact
            formId="CustomOfferModalBilling"
            initialValues={billingInitialValues || initialValuesFromBilling(null)}
            lockedFields={lockedBillingFields}
            inProgress={saveBillingDetailsInProgress}
            saveError={saveBillingDetailsError}
            onSubmit={billing =>
              onSaveBillingDetails(billing)
                .then(() => onRetryVatPreview(paymentMethod))
                .catch(() => null)
            }
          />
        ) : null}
      </div>
    </div>
  ) : null;

  // The offer step stops only for what no billing detail can fix: a blocked
  // sale or the freelancer's missing tax details. Every other VAT state waits
  // for the confirm step (invoice) or the checkout (card), spec §2.
  const hardBlocked = isCustomer && !!notice && notice.kind === 'blocked';
  const offerStepButtonId =
    paymentMethod === 'invoice'
      ? 'CustomOfferModal.continue'
      : isLegacyPro
      ? 'CustomOfferForm.acceptAnOffer'
      : 'CustomOfferModal.continueToPayment';

  const acceptErrorMessage = acceptOfferError ? (
    <p className={css.acceptOfferError}>
      {acceptGateNotice ? (
        <FormattedMessage id={acceptGateNotice.messageId} values={noticeValues} />
      ) : (
        <FormattedMessage id="CustomOfferModal.acceptFailed" />
      )}
    </p>
  ) : null;

  // The work and the service fee rows open both customer boxes. Under the old
  // provider commission the customer has no fee row; a pending offer is never
  // priced that way, so the VAT-free box always shows it.
  const customerWorkAndFeeRows = (
    <>
      <div className={css.modalPricingRow}>
        <span className={css.modalPricingLabel}>
          <FormattedMessage id="BookingBreakdown.subTotalCustomer" />
        </span>
        <span className={css.modalPricingValue}>{money(totals.net)}</span>
      </div>
      {!isProviderFeeModel ? (
        <div className={css.modalPricingRow}>
          <span className={css.modalPricingLabel}>
            <FormattedMessage
              id="BookingBreakdown.serviceFeeCustomer"
              values={{ percentage: totals.feePercentage }}
            />
          </span>
          <span className={css.modalPricingValue}>{money(totals.fee)}</span>
        </div>
      ) : null}
    </>
  );

  // Choosing how to pay, the customer sees the price without VAT only: the
  // VAT depends on the method, so nothing here changes when the method does.
  const customerPricingExclVat = (
    <div className={css.modalPricingBox}>
      {customerWorkAndFeeRows}
      <div className={css.modalPricingRowTotal}>
        <span className={css.modalPricingLabelTotal}>
          <FormattedMessage id="BookingBreakdown.totalExclVat" />
        </span>
        <span className={css.modalPricingValueTotal}>{money(totals.customerPrice)}</span>
      </div>
    </div>
  );

  // The customer's VAT-inclusive box: an accepted offer's stored line items,
  // a pending invoice offer on the confirm step once its preview is ok, or a
  // pending legacy pro offer (its accept has no confirm step).
  const customerPricingWithVat = (
    <div className={css.modalPricingBox}>
      {customerWorkAndFeeRows}
      {customerVatRows
        .filter(row => row.amount > 0)
        .map(row => (
          <div key={row.key} className={css.modalPricingRow}>
            <span className={css.modalPricingLabel}>
              <FormattedMessage id={row.messageId} values={{ rateLabel: row.rateLabel }} />
            </span>
            <span className={css.modalPricingValue}>{money(row.amount)}</span>
          </div>
        ))}
      <div className={css.modalPricingRowTotal}>
        <span className={css.modalPricingLabelTotal}>
          <FormattedMessage id="BookingBreakdown.total" />
        </span>
        <span className={css.modalPricingValueTotal}>
          {/* A pending offer's payinTotal can be left over from a cancelled
              card payment; only an accepted one prints it, or a legacy pro
              offer, which has no confirm step and always printed it. */}
          {(!isPending || isLegacyPro) && customOfferData.payinTotal
            ? formatMoney(intl, customOfferData.payinTotal)
            : money(totals.customerTotal)}
        </span>
      </div>
    </div>
  );

  const vatExplanation =
    isCustomer && preview && preview.status === GATE_STATUS.OK ? (
      <VatExplanation
        billing={billingInitialValues || initialValuesFromBilling(null)}
        buyerValidation={preview.buyerValidation}
        path={preview.path}
        lockedFields={lockedBillingFields}
        formId="CustomOfferModalBillingEdit"
        onSaveBillingDetails={onSaveBillingDetails}
        saveBillingDetailsInProgress={saveBillingDetailsInProgress}
        saveBillingDetailsError={saveBillingDetailsError}
        onBillingSaved={() => onRetryVatPreview(paymentMethod)}
      />
    ) : null;

  // The confirm step is for a pending invoice offer of the customer only; if
  // any of that stops being true, the offer step renders instead of an
  // accept that no longer fits.
  if (
    step === 'confirm' &&
    isCustomer &&
    isPending &&
    !isLegacyPro &&
    paymentMethod === 'invoice'
  ) {
    // After billing details are saved the refetch is in flight while the
    // previous ok preview is still in state: it belongs to the old billing, so
    // the step shows the check and blocks the accept until the new one lands.
    const confirmReady = vatReady && !vatPreviewInProgress;
    return (
      <Modal
        id="CustomOfferModal"
        isOpen={isOpen}
        onClose={onClose}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.customOfferModalContent}>
          <CustomOfferConfirmStep
            offerTitle={customOfferData.title}
            gateNotice={vatNoticeBlock}
            checking={!notice && (!preview || vatPreviewInProgress)}
            vatExplanation={vatPreviewInProgress ? null : vatExplanation}
            pricingBox={confirmReady ? customerPricingWithVat : null}
            paymentTermDays={paymentTermDays}
            acceptError={acceptErrorMessage}
            onBack={() => setStep('offer')}
            onAccept={() => onAcceptCustomOffer('invoice')}
            acceptInProgress={acceptOfferInProgress}
            acceptDisabled={acceptOfferInProgress || !confirmReady}
            showHoldHint={isHeld && !vatReady}
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      id="CustomOfferModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      // containerClassName={css.customOfferModalContainer}
      usePortal
    >
      <div className={css.customOfferModalContent}>
        <h2 className={css.customOfferModalTitle}>
          <FormattedMessage id="ProcessSteps.offerDetails" />
          {isPending && (
            <span className={css.pendingStatusBadge}>
              <FormattedMessage id="ProcessSteps.pendingApproval" />
            </span>
          )}
        </h2>

        {customOfferData.title ? (
          <div className={css.modalSection}>
            <h3 className={css.modalOfferTitle}>{customOfferData.title}</h3>
          </div>
        ) : null}

        {customOfferData.description && (
          <div className={css.modalSection}>
            <div className={css.modalDescriptionBox}>
              <p className={css.modalDescriptionText}>{customOfferData.description}</p>
            </div>
          </div>
        )}

        {attachments.length > 0 ? (
          <div className={css.modalSection}>
            <h3 className={css.modalAttachmentsTitle}>
              <FormattedMessage id="CustomOfferModal.attachmentsTitle" />
            </h3>
            <AttachmentList
              attachments={attachments}
              intl={intl}
              onDownloadAttachment={onDownloadAttachment}
              downloadAttachmentInProgressIds={downloadAttachmentInProgressIds}
              onOpenAttachment={onOpenAttachment}
              onFetchAttachmentPreview={onFetchAttachmentPreview}
              attachmentPreviewUrls={attachmentPreviewUrls}
            />
          </div>
        ) : null}

        {/* The schedule, carded like the pricing below it so the dates read as
            a decision input rather than fine print. Offers made before the
            start date existed show the delivery column alone. */}
        {customOfferData.deliveryDate && (
          <div className={css.modalSection}>
            <div className={css.modalScheduleBox}>
              {customOfferData.startDate && (
                <div className={css.modalScheduleColumn}>
                  <span className={css.modalScheduleLabel}>
                    <FormattedMessage id="ProcessSteps.startDate" />
                  </span>
                  <span className={css.modalScheduleValue}>
                    {formatFullDate(intl, customOfferData.startDate)}
                  </span>
                </div>
              )}
              <div className={css.modalScheduleColumn}>
                <span className={css.modalScheduleLabel}>
                  <FormattedMessage id="ProcessSteps.deliveryDate" />
                </span>
                <span className={css.modalScheduleValue}>
                  {formatFullDate(intl, customOfferData.deliveryDate)}
                </span>
              </div>
            </div>
          </div>
        )}

        {!isCustomer || hardBlocked ? vatNoticeBlock : null}

        <div className={css.modalSection}>
          {isCustomer && isPending && !isLegacyPro ? (
            customerPricingExclVat
          ) : isCustomer ? (
            customerPricingWithVat
          ) : (
            <div className={css.modalPricingBox}>
              {isPending ? (
                // The VAT still depends on the customer's payment method and
                // billing details, so the provider sees the same two VAT 0 %
                // prices as in the offer form until the offer is accepted.
                <>
                  <div className={css.modalPricingRow}>
                    <span className={css.modalPricingLabel}>
                      <FormattedMessage id="CustomOfferForm.amountLabel" />
                    </span>
                    <span className={css.modalPricingValue}>{money(totals.customerPrice)}</span>
                  </div>
                  <div className={css.modalNetPayoutRow}>
                    <span className={css.modalNetPayoutLabel}>
                      <FormattedMessage id="BookingBreakdown.providerPrice" />
                    </span>
                    <span className={css.modalNetPayoutValue}>{money(totals.providerNet)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className={css.modalPricingRow}>
                    <span className={css.modalPricingLabel}>
                      <FormattedMessage id="BookingBreakdown.customerPrice" />
                    </span>
                    <span className={css.modalPricingValue}>{money(totals.customerTotal)}</span>
                  </div>
                  <div className={css.modalPricingRow}>
                    <span className={css.modalPricingLabel}>
                      {!isProviderFeeModel ? (
                        <FormattedMessage id="BookingBreakdown.serviceFeeProvider" />
                      ) : totals.feeVat > 0 ? (
                        <FormattedMessage
                          id="ProcessSteps.platformFeeInclVat"
                          values={{ rateLabel: rateLabelFor(legacyFeeSupply, intl.locale) }}
                        />
                      ) : (
                        <FormattedMessage id="ProcessSteps.platformFee" />
                      )}
                    </span>
                    <span className={css.modalPricingValue}>
                      {money(totals.fee + totals.feeVat)}
                    </span>
                  </div>
                  <div className={css.modalNetPayoutRow}>
                    <span className={css.modalNetPayoutLabel}>
                      <FormattedMessage
                        id={
                          isProviderFeeModel
                            ? 'ProcessSteps.netPayout'
                            : 'BookingBreakdown.providerPrice'
                        }
                      />
                    </span>
                    <span className={css.modalNetPayoutValue}>{money(totals.providerNet)}</span>
                  </div>
                  {totals.providerVat > 0 ? (
                    <div className={css.modalPricingRow}>
                      <span className={css.modalPricingLabel}>
                        <FormattedMessage
                          id="BookingBreakdown.providerVat"
                          values={{ rateLabel: providerRateLabel }}
                        />
                      </span>
                      <span className={css.modalPricingValue}>{money(totals.providerVat)}</span>
                    </div>
                  ) : null}
                  <div className={css.modalPricingRowTotal}>
                    <span className={css.modalPricingLabelTotal}>
                      <FormattedMessage id="ProcessSteps.youWillEarnFinal" />
                    </span>
                    <span className={css.modalPricingValueTotal}>
                      {customOfferData.payoutTotal
                        ? formatMoney(intl, customOfferData.payoutTotal)
                        : money(totals.payout)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
          {isCustomer && isPending && !isLegacyPro ? (
            <p className={css.modalHelpNote}>
              <FormattedMessage id="CustomOfferModal.vatCalculatedNextStep" />
            </p>
          ) : null}
          {!isCustomer && isPending ? (
            <p className={css.modalHelpNote}>
              <FormattedMessage id="CustomOfferForm.vatCalculatedOnAccept" />
            </p>
          ) : null}
        </div>
        {isCustomer && isPending && invoiceOption ? (
          <div className={css.modalSection}>
            <h3 className={css.paymentMethodTitle} id="custom-offer-payment-method">
              <FormattedMessage id="CustomOfferModal.paymentMethodTitle" />
            </h3>
            <div
              className={css.paymentMethodOptions}
              role="radiogroup"
              aria-labelledby="custom-offer-payment-method"
            >
              <button
                type="button"
                role="radio"
                aria-checked={paymentMethod === 'card'}
                className={classNames(css.paymentMethodOption, {
                  [css.paymentMethodOptionSelected]: paymentMethod === 'card',
                })}
                onClick={() => setPaymentMethod('card')}
              >
                <FormattedMessage id="CustomOfferModal.payByCard" />
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={paymentMethod === 'invoice'}
                disabled={invoiceDisabled}
                className={classNames(css.paymentMethodOption, {
                  [css.paymentMethodOptionSelected]: paymentMethod === 'invoice',
                })}
                onClick={onInvoiceClick}
              >
                <FormattedMessage id="CustomOfferModal.payByInvoice" />
                {invoiceNoteId ? (
                  <span className={css.paymentMethodNote}>
                    <FormattedMessage id={invoiceNoteId} />
                  </span>
                ) : null}
              </button>
            </div>
          </div>
        ) : null}
        {/* Payment flow explanation for customer - only show when pending */}
        {isCustomer && isPending && (
          <div className={css.modalSection}>
            <div className={css.modalPaymentExplanation}>
              <h3 className={css.modalPaymentExplanationTitle}>
                <HiLightBulb className={css.lightBulbIcon} />
                <FormattedMessage id="CustomOfferForm.paymentFlowTitle" />
              </h3>
              <p className={css.modalPaymentExplanationText}>
                {paymentMethod === 'invoice' ? (
                  <FormattedMessage
                    id="TransactionPanel.acceptProModalMessage"
                    values={{ days: paymentTermDays }}
                  />
                ) : (
                  <FormattedMessage
                    id="CustomOfferForm.customerPaymentExplanation"
                    values={{ b: chunks => <b>{chunks}</b> }}
                  />
                )}
              </p>
            </div>
          </div>
        )}
        {isCustomer && onAcceptCustomOffer && isPending && (
          <div className={css.modalActionButtons}>
            {/* Only the legacy pro accept can fail here; the card path is a
                redirect, and an invoice failure belongs to the confirm step. */}
            {isLegacyPro ? acceptErrorMessage : null}
            <PrimaryButton
              onClick={() =>
                paymentMethod === 'invoice'
                  ? setStep('confirm')
                  : onAcceptCustomOffer(paymentMethod)
              }
              inProgress={acceptOfferInProgress && paymentMethod !== 'invoice'}
              disabled={acceptOfferInProgress || hardBlocked}
            >
              <FormattedMessage id={offerStepButtonId} />
            </PrimaryButton>
          </div>
        )}
        {!isCustomer && onUpdateOffer && isPending && (
          <div className={css.modalActionButtons}>
            <PrimaryButton onClick={onUpdateOffer}>
              <FormattedMessage id="CustomOfferForm.updateAnOffer" />
            </PrimaryButton>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CustomOfferModal;
